import {
  DURASPEED_SWITCH_ID,
  UI_DUMP_PATH,
} from '@mxa-argos/modules/forja/constants/forja.constants';
import type { ForjaUISwitch } from '@mxa-argos/modules/forja/interfaces/forja-ui-switch.interface';
import type { ForjaDeviceSession } from '@mxa-argos/modules/forja/lib/engine/forja-device-session';
import { ForjaError } from '@mxa-argos/modules/forja/lib/forja-error';
import { sleep } from '@mxa-argos/modules/forja/lib/sleep';

const NODE_PATTERN = /<node[^>]*>/g;
const BOUNDS_PATTERN = /bounds="\[(\d+),(\d+)\]\[(\d+),(\d+)\]"/;
const TEXT_PATTERN = / text="([^"]+)"/;
const SCROLL_ATTEMPTS = 6;
const SWIPE_DURATION_MS = 120;
const UI_SETTLE_TIMEOUT_MS = 15_000;
const UI_POLL_MS = 400;

interface NodeBounds {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

export class UIAutomator {
  constructor(readonly device: ForjaDeviceSession) {}

  // Do not dump to /dev/tty: with no terminal attached it produces nothing and still reports
  // success.
  async dumpUI(): Promise<string> {
    const output = await this.device.shell(
      `uiautomator dump ${UI_DUMP_PATH} >/dev/null 2>&1; cat ${UI_DUMP_PATH}`,
    );

    if (!output.includes('<hierarchy')) {
      throw new ForjaError(
        'UI_AUTOMATION_FAILED',
        `could not read the UI tree: ${output.slice(0, 200) || '<no output>'}`,
      );
    }

    return output;
  }

  async tapNodeByText(text: string): Promise<void> {
    const deadline = Date.now() + UI_SETTLE_TIMEOUT_MS;
    let lastSeenLabels: string[] = [];

    while (Date.now() < deadline) {
      const xml = await this.searchUITree();

      if (xml) {
        lastSeenLabels = readLabels(xml);
        const node = findNodeByText(xml, text);

        if (node) {
          await this.tapCenter(node);
          return;
        }
      }

      await sleep(UI_POLL_MS);
    }

    throw new ForjaError(
      'UI_AUTOMATION_FAILED',
      `UI node with text '${text}' not found. On screen: ${lastSeenLabels.join(' | ') || '<no readable UI tree>'}`,
    );
  }

  async waitForSwitches(): Promise<void> {
    const deadline = Date.now() + UI_SETTLE_TIMEOUT_MS;

    while (Date.now() < deadline) {
      if ((await this.searchUITree())?.includes(DURASPEED_SWITCH_ID)) {
        return;
      }
      await sleep(UI_POLL_MS);
    }

    throw new ForjaError('UI_AUTOMATION_FAILED', 'the DuraSpeed app list never rendered');
  }

  async scrollToTop(): Promise<void> {
    for (let attempt = 0; attempt < SCROLL_ATTEMPTS; attempt += 1) {
      await this.swipe(400, 1000);
    }
  }

  async scrollDown(): Promise<void> {
    await this.swipe(1000, 400);
  }

  async tapSwitch(labelSwitch: ForjaUISwitch): Promise<void> {
    await this.device.shell(
      'input',
      'tap',
      String(labelSwitch.centerX),
      String(labelSwitch.centerY),
    );
  }

  private async searchUITree(): Promise<string | null> {
    return this.dumpUI().catch(() => null);
  }

  private async swipe(fromY: number, toY: number): Promise<void> {
    await this.device.shell(
      'input',
      'swipe',
      '360',
      String(fromY),
      '360',
      String(toY),
      String(SWIPE_DURATION_MS),
    );
  }

  private async tapCenter(bounds: NodeBounds): Promise<void> {
    const centerX = Math.floor((bounds.left + bounds.right) / 2);
    const centerY = Math.floor((bounds.top + bounds.bottom) / 2);
    await this.device.shell('input', 'tap', String(centerX), String(centerY));
  }
}

export function readLabels(xml: string): string[] {
  return (xml.match(NODE_PATTERN) ?? [])
    .map((node) => node.match(TEXT_PATTERN)?.[1])
    .filter((label): label is string => label !== undefined);
}

export function searchLabeledSwitch(xml: string, label: string): ForjaUISwitch | null {
  let labelCenterY: number | null = null;
  const switches: (ForjaUISwitch & { top: number; bottom: number })[] = [];

  for (const node of xml.match(NODE_PATTERN) ?? []) {
    const bounds = parseBounds(node);
    if (!bounds) {
      continue;
    }

    const text = node.match(TEXT_PATTERN);
    if (text && text[1] === label) {
      labelCenterY = Math.floor((bounds.top + bounds.bottom) / 2);
    }

    if (node.includes(`resource-id="${DURASPEED_SWITCH_ID}"`)) {
      switches.push({
        top: bounds.top,
        bottom: bounds.bottom,
        centerX: Math.floor((bounds.left + bounds.right) / 2),
        centerY: Math.floor((bounds.top + bounds.bottom) / 2),
        isChecked: node.includes('checked="true"'),
      });
    }
  }

  if (labelCenterY === null) {
    return null;
  }

  const rowCenterY = labelCenterY;
  const matched = switches.find(
    (candidate) => candidate.top <= rowCenterY && rowCenterY <= candidate.bottom,
  );

  if (!matched) {
    return null;
  }

  return { centerX: matched.centerX, centerY: matched.centerY, isChecked: matched.isChecked };
}

function findNodeByText(xml: string, text: string): NodeBounds | null {
  for (const node of xml.match(NODE_PATTERN) ?? []) {
    if (!node.includes(`text="${text}"`)) {
      continue;
    }

    const bounds = parseBounds(node);

    if (bounds) {
      return bounds;
    }
  }

  return null;
}

function parseBounds(node: string): NodeBounds | null {
  const bounds = node.match(BOUNDS_PATTERN);

  if (!bounds) {
    return null;
  }

  return {
    left: Number(bounds[1]),
    top: Number(bounds[2]),
    right: Number(bounds[3]),
    bottom: Number(bounds[4]),
  };
}
